import * as React3 from 'react';
import { forwardRef, useState, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { X, Info, AlertCircle, XCircle, CheckCircle, AlertTriangle, ChevronLeft, ChevronRight, Check, Filter, ChevronUp, ChevronDown, Calendar, Home, Pause, Play, Trash2, ChevronsUpDown, ArrowUp, ArrowDown, Monitor, Moon, Sun } from 'lucide-react';
import Editor from '@monaco-editor/react';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-[hsl(var(--accent-hover))]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover-menu",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover-icon",
        link: "text-ugoo-brand-sky underline-offset-4 hover-link hover:underline"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React3.forwardRef(
  ({ className, variant, size, ...props }, ref) => {
    return /* @__PURE__ */ jsx("button", { className: cn(buttonVariants({ variant, size, className })), ref, ...props });
  }
);
Button.displayName = "Button";
var Input = React3.forwardRef(
  ({ className, label, error, helperText, type, ...props }, ref) => {
    return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
      label && /* @__PURE__ */ jsx("label", { className: "text-sm font-medium text-foreground", children: label }),
      /* @__PURE__ */ jsx(
        "input",
        {
          type,
          className: cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground",
            "placeholder:text-muted-foreground",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus:ring-destructive",
            className
          ),
          ref,
          ...props
        }
      ),
      error && /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: error }),
      !error && helperText && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: helperText })
    ] });
  }
);
Input.displayName = "Input";
var Card = React3.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn("rounded-lg border bg-card text-card-foreground shadow-sm", className),
      ...props
    }
  )
);
Card.displayName = "Card";
var CardHeader = React3.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("flex flex-col space-y-1.5 p-4 sm:p-6", className), ...props })
);
CardHeader.displayName = "CardHeader";
var CardTitle = React3.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "h3",
    {
      ref,
      className: cn(
        "text-lg sm:text-xl md:text-2xl font-semibold leading-none tracking-tight",
        className
      ),
      ...props
    }
  )
);
CardTitle.displayName = "CardTitle";
var CardDescription = React3.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("p", { ref, className: cn("text-xs sm:text-sm text-muted-foreground", className), ...props }));
CardDescription.displayName = "CardDescription";
var CardContent = React3.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-4 sm:p-6 pt-0", className), ...props })
);
CardContent.displayName = "CardContent";
var CardFooter = React3.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("flex items-center p-4 sm:p-6 pt-0", className), ...props })
);
CardFooter.displayName = "CardFooter";
var badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-secondary text-secondary-foreground",
        success: "border-transparent bg-green-600 dark:bg-green-700 text-white",
        error: "border-transparent bg-red-600 dark:bg-red-700 text-white",
        warning: "border-transparent bg-amber-500 dark:bg-amber-600 text-white",
        info: "border-transparent bg-blue-600 dark:bg-blue-700 text-white",
        outline: "text-foreground",
        destructive: "border-transparent bg-destructive text-destructive-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn(badgeVariants({ variant }), className), ...props });
}
function Spinner({ size = "md", className }) {
  const sizeClasses = {
    sm: "h-4 w-4 border-2",
    md: "h-8 w-8 border-2",
    lg: "h-12 w-12 border-4"
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "animate-spin rounded-full border-primary border-t-transparent",
        sizeClasses[size],
        className
      )
    }
  );
}
var Textarea = React3.forwardRef(
  ({ className, label, error, ...props }, ref) => {
    return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
      label && /* @__PURE__ */ jsx("label", { className: "text-sm font-medium text-foreground", children: label }),
      /* @__PURE__ */ jsx(
        "textarea",
        {
          className: cn(
            "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground",
            "placeholder:text-muted-foreground",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus:ring-destructive",
            className
          ),
          ref,
          ...props
        }
      ),
      error && /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: error })
    ] });
  }
);
Textarea.displayName = "Textarea";
var Select = React3.forwardRef(
  ({ className, label, error, options, ...props }, ref) => {
    return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
      label && /* @__PURE__ */ jsx("label", { className: "text-sm font-medium text-foreground", children: label }),
      /* @__PURE__ */ jsx(
        "select",
        {
          className: cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus:ring-destructive",
            className
          ),
          ref,
          ...props,
          children: options.map((option) => /* @__PURE__ */ jsx("option", { value: option.value, children: option.label }, option.value))
        }
      ),
      error && /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: error })
    ] });
  }
);
Select.displayName = "Select";
function Checkbox({
  id,
  label,
  checked,
  onChange,
  disabled = false,
  className
}) {
  const handleChange = (e) => {
    onChange(e.target.checked);
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2", className), children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "checkbox",
        id,
        checked,
        onChange: handleChange,
        disabled,
        className: cn(
          "h-4 w-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0",
          "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
          "transition-colors"
        )
      }
    ),
    label && /* @__PURE__ */ jsx(
      "label",
      {
        htmlFor: id,
        className: cn(
          "text-sm font-medium leading-none cursor-pointer select-none",
          disabled && "cursor-not-allowed opacity-50"
        ),
        children: label
      }
    )
  ] });
}
function Switch({ checked, onCheckedChange, label, disabled }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center space-x-3", children: [
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        role: "switch",
        "aria-checked": checked,
        disabled,
        onClick: () => onCheckedChange(!checked),
        className: cn(
          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background",
          "disabled:cursor-not-allowed disabled:opacity-50",
          checked ? "bg-primary" : "bg-input"
        ),
        children: /* @__PURE__ */ jsx(
          "span",
          {
            className: cn(
              "inline-block h-4 w-4 transform rounded-full bg-background transition-transform shadow-sm",
              checked ? "translate-x-6" : "translate-x-1"
            )
          }
        )
      }
    ),
    label && /* @__PURE__ */ jsx("label", { className: "text-sm font-medium text-foreground", children: label })
  ] });
}
function Dialog({ open, onOpenChange, children }) {
  if (!open) return null;
  return /* @__PURE__ */ jsxs("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6", children: [
    /* @__PURE__ */ jsx("div", { className: "fixed inset-0 bg-black/50", onClick: () => onOpenChange(false) }),
    children
  ] });
}
function DialogContent({ children, className }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "relative z-50 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-card rounded-lg shadow-lg",
        "animate-in fade-in-0 zoom-in-95",
        className
      ),
      children
    }
  );
}
function DialogHeader({ children }) {
  return /* @__PURE__ */ jsx("div", { className: "flex flex-col space-y-1.5 p-4 sm:p-6 border-b border-border", children });
}
function DialogTitle({ children }) {
  return /* @__PURE__ */ jsx("h2", { className: "text-base sm:text-lg font-semibold text-card-foreground pr-8", children });
}
function DialogDescription({ children }) {
  return /* @__PURE__ */ jsx("p", { className: "text-xs sm:text-sm text-muted-foreground", children });
}
function DialogFooter({ children }) {
  return /* @__PURE__ */ jsx("div", { className: "flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:space-x-2 p-4 sm:p-6 border-t border-border", children });
}
function DialogClose({ onClick }) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      onClick,
      className: "absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground",
      children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
    }
  );
}
function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  onConfirm,
  variant = "default",
  icon = "warning",
  isLoading = false
}) {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };
  const iconComponents = {
    warning: /* @__PURE__ */ jsx(AlertTriangle, { className: "h-6 w-6 text-yellow-600 dark:text-yellow-400" }),
    delete: /* @__PURE__ */ jsx(Trash2, { className: "h-6 w-6 text-red-600 dark:text-red-400" }),
    execute: /* @__PURE__ */ jsx(Play, { className: "h-6 w-6 text-blue-600 dark:text-blue-400" }),
    toggle: /* @__PURE__ */ jsx(Pause, { className: "h-6 w-6 text-gray-600 dark:text-gray-400" }),
    check: /* @__PURE__ */ jsx(Check, { className: "h-6 w-6 text-green-600 dark:text-green-400" })
  };
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(DialogContent, { children: [
    /* @__PURE__ */ jsxs(DialogHeader, { children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center space-x-3 mb-2", children: [
        /* @__PURE__ */ jsx("div", { className: "flex-shrink-0", children: iconComponents[icon] }),
        /* @__PURE__ */ jsx(DialogTitle, { children: title })
      ] }),
      /* @__PURE__ */ jsx(DialogDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsxs(DialogFooter, { children: [
      /* @__PURE__ */ jsx(Button, { variant: "outline", onClick: () => onOpenChange(false), disabled: isLoading, children: cancelText }),
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: variant === "destructive" ? "destructive" : "default",
          onClick: handleConfirm,
          disabled: isLoading,
          children: isLoading ? "Processando..." : confirmText
        }
      )
    ] })
  ] }) });
}
var icons = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertCircle,
  info: Info
};
var colors = {
  success: "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-green-900 dark:text-green-100",
  error: "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 text-red-900 dark:text-red-100",
  warning: "bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800 text-yellow-900 dark:text-yellow-100",
  info: "bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100"
};
var iconColors = {
  success: "text-green-600 dark:text-green-400",
  error: "text-red-600 dark:text-red-400",
  warning: "text-yellow-600 dark:text-yellow-400",
  info: "text-blue-600 dark:text-blue-400"
};
function Toast({ type, message, description, onClose }) {
  const Icon = icons[type];
  React3.useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5e3);
    return () => clearTimeout(timer);
  }, [onClose]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "fixed bottom-4 right-4 z-50 w-full max-w-md",
        "border rounded-lg shadow-lg p-4",
        "animate-in slide-in-from-right",
        colors[type]
      ),
      children: /* @__PURE__ */ jsxs("div", { className: "flex items-start space-x-3", children: [
        /* @__PURE__ */ jsx(Icon, { className: cn("h-5 w-5 flex-shrink-0 mt-0.5", iconColors[type]) }),
        /* @__PURE__ */ jsxs("div", { className: "flex-1", children: [
          /* @__PURE__ */ jsx("p", { className: "font-medium", children: message }),
          description && /* @__PURE__ */ jsx("p", { className: "text-sm mt-1 opacity-90", children: description })
        ] }),
        /* @__PURE__ */ jsx(
          "button",
          {
            onClick: onClose,
            className: "flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity",
            children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
          }
        )
      ] })
    }
  );
}
function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  isLoading = false,
  variant = "warning"
}) {
  if (!isOpen) return null;
  const variantColors = {
    danger: "text-red-600",
    warning: "text-orange-600",
    info: "text-blue-600"
  };
  return /* @__PURE__ */ jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50", children: /* @__PURE__ */ jsxs(Card, { className: "w-full max-w-md", children: [
    /* @__PURE__ */ jsx(CardHeader, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
      /* @__PURE__ */ jsx(AlertTriangle, { className: `w-6 h-6 ${variantColors[variant]}` }),
      /* @__PURE__ */ jsx(CardTitle, { children: title })
    ] }) }),
    /* @__PURE__ */ jsxs(CardContent, { className: "space-y-4", children: [
      /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: description }),
      /* @__PURE__ */ jsxs("div", { className: "flex gap-2 pt-4", children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            onClick: onConfirm,
            disabled: isLoading,
            variant: variant === "danger" ? "destructive" : "default",
            children: isLoading ? /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(Spinner, { size: "sm", className: "mr-2" }),
              "Processando..."
            ] }) : confirmText
          }
        ),
        /* @__PURE__ */ jsx(Button, { variant: "outline", onClick: onClose, disabled: isLoading, children: cancelText })
      ] })
    ] })
  ] }) });
}
var TableContext = React3.createContext(null);
var getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
};
function Table({ children, className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsx("table", { className: cn("w-full", className), ...props, children }) });
}
function SortableTable({
  data,
  children,
  defaultSortKey,
  defaultSortDirection = "asc"
}) {
  const [sortKey, setSortKey] = React3.useState(defaultSortKey || null);
  const [sortDirection, setSortDirection] = React3.useState(
    defaultSortKey ? defaultSortDirection : null
  );
  const handleSort = React3.useCallback(
    (key) => {
      if (sortKey !== key) {
        setSortKey(key);
        setSortDirection("asc");
      } else {
        if (sortDirection === "asc") {
          setSortDirection("desc");
        } else if (sortDirection === "desc") {
          setSortKey(null);
          setSortDirection(null);
        } else {
          setSortDirection("asc");
        }
      }
    },
    [sortKey, sortDirection]
  );
  const sortedData = React3.useMemo(() => {
    if (!sortKey || !sortDirection) {
      return data;
    }
    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, sortKey);
      const bValue = getNestedValue(b, sortKey);
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === "asc" ? 1 : -1;
      if (bValue == null) return sortDirection === "asc" ? -1 : 1;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (sortDirection === "asc") {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [data, sortKey, sortDirection]);
  const contextValue = {
    sortKey,
    sortDirection,
    onSort: handleSort
  };
  return /* @__PURE__ */ jsx(TableContext.Provider, { value: contextValue, children: children(sortedData) });
}
function TableHeader({ children, className, ...props }) {
  return /* @__PURE__ */ jsx("thead", { className, ...props, children });
}
function TableBody({ children, className, ...props }) {
  return /* @__PURE__ */ jsx("tbody", { className, ...props, children });
}
function TableRow({ children, className, highlight, ...props }) {
  return /* @__PURE__ */ jsx(
    "tr",
    {
      className: cn(
        "border-b border-border transition-all duration-150 hover-table-row",
        highlight && "bg-red-50/50 dark:bg-red-950/20",
        className
      ),
      ...props,
      children
    }
  );
}
function TableHead({ children, className, sortable, sortKey, align = "left", ...props }) {
  const context = React3.useContext(TableContext);
  const isSorted = context?.sortKey === sortKey;
  const sortDirection = isSorted ? context?.sortDirection : null;
  const handleClick = () => {
    if (sortable && sortKey && context) {
      context.onSort(sortKey);
    }
  };
  const SortIcon = () => {
    if (!sortable) return null;
    if (!isSorted || sortDirection === null) {
      return /* @__PURE__ */ jsx(ChevronsUpDown, { className: "h-4 w-4 ml-1 shrink-0 text-muted-foreground/50" });
    }
    if (sortDirection === "asc") {
      return /* @__PURE__ */ jsx(ArrowUp, { className: "h-4 w-4 ml-1 shrink-0 text-primary" });
    }
    return /* @__PURE__ */ jsx(ArrowDown, { className: "h-4 w-4 ml-1 shrink-0 text-primary" });
  };
  const alignClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right"
  }[align];
  const justifyClass = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end"
  }[align];
  return /* @__PURE__ */ jsx(
    "th",
    {
      className: cn(
        "py-3 px-4 text-sm font-medium text-muted-foreground",
        alignClass,
        sortable && "cursor-pointer select-none hover:text-foreground transition-colors",
        className
      ),
      onClick: handleClick,
      ...props,
      children: /* @__PURE__ */ jsxs("div", { className: cn("flex items-center", justifyClass), children: [
        children,
        /* @__PURE__ */ jsx(SortIcon, {})
      ] })
    }
  );
}
function TableCell({ children, className, ...props }) {
  return /* @__PURE__ */ jsx("td", { className: cn("py-3 px-4", className), ...props, children });
}
function Pagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange
}) {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between mt-4 pt-4 border-t", children: [
    /* @__PURE__ */ jsxs("div", { className: "text-sm text-muted-foreground", children: [
      "Mostrando ",
      startItem,
      " at\xE9 ",
      endItem,
      " de ",
      totalItems,
      " resultados"
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex space-x-2", children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: "outline",
          size: "sm",
          onClick: () => onPageChange(currentPage - 1),
          disabled: currentPage === 1,
          children: /* @__PURE__ */ jsx(ChevronLeft, { className: "h-4 w-4" })
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          variant: "outline",
          size: "sm",
          onClick: () => onPageChange(currentPage + 1),
          disabled: currentPage >= totalPages,
          children: /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" })
        }
      )
    ] })
  ] });
}
var TabsContext = React3.createContext({
  activeTab: "",
  setActiveTab: () => {
  }
});
function Tabs({ defaultValue, className, children }) {
  const [activeTab, setActiveTab] = React3.useState(defaultValue);
  return /* @__PURE__ */ jsx(TabsContext.Provider, { value: { activeTab, setActiveTab }, children: /* @__PURE__ */ jsx("div", { className: cn("w-full", className), children }) });
}
function TabsList({ className, children }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
        className
      ),
      children
    }
  );
}
function TabsTrigger({ value, className, children }) {
  const { activeTab, setActiveTab } = React3.useContext(TabsContext);
  const isActive = activeTab === value;
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: () => setActiveTab(value),
      className: cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        isActive ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
        className
      ),
      children
    }
  );
}
function TabsContent({ value, className, children }) {
  const { activeTab } = React3.useContext(TabsContext);
  if (activeTab !== value) return null;
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      ),
      children
    }
  );
}
function Breadcrumbs({ items, LinkComponent, homeHref = "/" }) {
  const renderLink = (href, children, className) => {
    if (LinkComponent) {
      return /* @__PURE__ */ jsx(LinkComponent, { to: href, className, children });
    }
    return /* @__PURE__ */ jsx("a", { href, className, children });
  };
  return /* @__PURE__ */ jsxs("nav", { className: "flex items-center space-x-2 text-sm text-muted-foreground mb-4", children: [
    renderLink(
      homeHref,
      /* @__PURE__ */ jsx(Home, { className: "h-4 w-4" }),
      "flex items-center hover:text-foreground transition-colors"
    ),
    items.map((item, index) => /* @__PURE__ */ jsxs("div", { className: "flex items-center space-x-2", children: [
      /* @__PURE__ */ jsx(ChevronRight, { className: "h-4 w-4" }),
      item.href ? renderLink(item.href, item.label, "hover:text-foreground transition-colors") : /* @__PURE__ */ jsx("span", { className: "text-foreground font-medium", children: item.label })
    ] }, index))
  ] });
}
var colorStyles = {
  primary: {
    text: "text-primary",
    indicator: "bg-primary"
  },
  green: {
    text: "text-green-600 dark:text-green-400",
    indicator: "bg-green-600 dark:bg-green-400"
  },
  red: {
    text: "text-red-600 dark:text-red-400",
    indicator: "bg-red-600 dark:bg-red-400"
  },
  blue: {
    text: "text-blue-600 dark:text-blue-400",
    indicator: "bg-blue-600 dark:bg-blue-400"
  },
  yellow: {
    text: "text-yellow-600 dark:text-yellow-400",
    indicator: "bg-yellow-600 dark:bg-yellow-400"
  },
  gray: {
    text: "text-gray-600 dark:text-gray-400",
    indicator: "bg-gray-600 dark:bg-gray-400"
  }
};
function TabFilter({ tabs, currentTab, onChange }) {
  return /* @__PURE__ */ jsx("div", { className: "border-b border-border overflow-x-auto overflow-y-hidden", children: /* @__PURE__ */ jsx("div", { className: "flex space-x-4 sm:space-x-8 min-w-max pb-0", children: tabs.map((tab) => {
    const isActive = currentTab === tab.value;
    const color = tab.color || "primary";
    const styles = colorStyles[color];
    return /* @__PURE__ */ jsxs(
      "button",
      {
        className: `pb-3 px-1 text-sm font-medium transition-colors relative whitespace-nowrap ${isActive ? styles.text : "text-muted-foreground hover:text-foreground"}`,
        onClick: () => onChange(tab.value),
        children: [
          tab.label,
          " ",
          tab.count !== void 0 && `(${tab.count})`,
          isActive && /* @__PURE__ */ jsx("div", { className: `absolute bottom-0 left-0 right-0 h-0.5 ${styles.indicator}` })
        ]
      },
      tab.value
    );
  }) }) });
}
function ThemeToggle({ theme, setTheme, resolvedTheme, className }) {
  const cycleTheme = () => {
    if (theme === "light") setTheme("dark");
    else if (theme === "dark") setTheme("system");
    else setTheme("light");
  };
  const getIcon = () => {
    if (theme === "system") {
      return /* @__PURE__ */ jsx(Monitor, { className: "h-4 w-4" });
    }
    if (resolvedTheme === "dark" || theme === "dark") {
      return /* @__PURE__ */ jsx(Moon, { className: "h-4 w-4" });
    }
    return /* @__PURE__ */ jsx(Sun, { className: "h-4 w-4" });
  };
  const getTooltip = () => {
    if (theme === "system") return "System theme";
    if (theme === "dark") return "Dark mode";
    return "Light mode";
  };
  return /* @__PURE__ */ jsxs(
    Button,
    {
      variant: "ghost",
      size: "icon",
      onClick: cycleTheme,
      title: getTooltip(),
      className: className || "h-9 w-9",
      children: [
        getIcon(),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Toggle theme" })
      ]
    }
  );
}

// src/utils/formatters.ts
function truncate(text, length = 50) {
  if (text.length <= length) return text;
  return text.substring(0, length) + "...";
}
function formatNumber(num, locale = "pt-BR") {
  return new Intl.NumberFormat(locale).format(num);
}
function formatCurrency(value, currency = "BRL", locale = "pt-BR") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
function formatPercentage(num, decimals = 1) {
  return `${num.toFixed(decimals)}%`;
}
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error("Failed to copy:", error);
    return false;
  }
}
function removeMask(value) {
  return value.replace(/\D/g, "");
}
function applyPhoneMask(value) {
  const cleaned = value.replace(/\D/g, "");
  const limited = cleaned.substring(0, 13);
  if (limited.length <= 2) {
    return limited.length > 0 ? `+${limited}` : "";
  } else if (limited.length <= 4) {
    return `+${limited.substring(0, 2)} (${limited.substring(2)}`;
  } else if (limited.length <= 9) {
    return `+${limited.substring(0, 2)} (${limited.substring(2, 4)}) ${limited.substring(4)}`;
  } else {
    const ddd = limited.substring(2, 4);
    const firstPart = limited.substring(4, 9);
    const secondPart = limited.substring(9, 13);
    return `+${limited.substring(0, 2)} (${ddd}) ${firstPart}${secondPart ? `-${secondPart}` : ""}`;
  }
}
function formatToBRL(num) {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
}
function parseFromBRL(str) {
  const numbers = str.replace(/[^\d]/g, "");
  return numbers ? parseFloat(numbers) / 100 : 0;
}
var PhoneInput = forwardRef(
  ({ className, label, error, onChange, value = "", ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    useEffect(() => {
      if (value) {
        setDisplayValue(applyPhoneMask(value));
      }
    }, [value]);
    const handleChange = (e) => {
      const inputValue = e.target.value;
      const masked = applyPhoneMask(inputValue);
      setDisplayValue(masked);
      const cleaned = inputValue.replace(/\D/g, "");
      onChange?.(cleaned);
    };
    return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsx("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5", children: label }),
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "tel",
          className: cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-red-500 focus-visible:ring-red-500",
            className
          ),
          ref,
          value: displayValue,
          onChange: handleChange,
          placeholder: "+55 (11) 99999-9999",
          ...props
        }
      ),
      error && /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-sm text-destructive", children: error })
    ] });
  }
);
PhoneInput.displayName = "PhoneInput";
function validatePassword(password) {
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  return {
    hasMinLength,
    hasUpperCase,
    hasLowerCase,
    hasNumber,
    hasSpecialChar,
    isValid: hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar
  };
}
function PasswordStrength({ password }) {
  const validation = validatePassword(password);
  const requirements = [
    { label: "M\xEDnimo de 8 caracteres", met: validation.hasMinLength },
    { label: "1 letra mai\xFAscula", met: validation.hasUpperCase },
    { label: "1 letra min\xFAscula", met: validation.hasLowerCase },
    { label: "1 n\xFAmero", met: validation.hasNumber },
    { label: "1 caractere especial", met: validation.hasSpecialChar }
  ];
  if (!password) return null;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2 p-3 bg-muted/50 rounded-lg", children: [
    /* @__PURE__ */ jsx("p", { className: "text-xs font-medium text-muted-foreground", children: "Requisitos da senha:" }),
    /* @__PURE__ */ jsx("div", { className: "space-y-1", children: requirements.map((req) => /* @__PURE__ */ jsxs("div", { className: "flex items-center space-x-2", children: [
      req.met ? /* @__PURE__ */ jsx(Check, { className: "h-3 w-3 text-green-500" }) : /* @__PURE__ */ jsx(X, { className: "h-3 w-3 text-red-500" }),
      /* @__PURE__ */ jsx(
        "span",
        {
          className: `text-xs ${req.met ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}`,
          children: req.label
        }
      )
    ] }, req.label)) })
  ] });
}
var JsonEditor = React3.forwardRef(
  ({ label, error, value, onChange, height = "200px", readOnly = false, theme = "light" }, ref) => {
    const handleEditorChange = (value2) => {
      onChange?.(value2 || "");
    };
    return /* @__PURE__ */ jsxs("div", { className: "space-y-2", ref, children: [
      label && /* @__PURE__ */ jsx("label", { className: "text-sm font-medium text-foreground", children: label }),
      /* @__PURE__ */ jsx(
        "div",
        {
          className: cn(
            "rounded-md border border-input overflow-hidden",
            "focus-within:ring-2 focus-within:ring-ring focus-within:border-transparent",
            error && "border-destructive focus-within:ring-destructive"
          ),
          children: /* @__PURE__ */ jsx(
            Editor,
            {
              height,
              defaultLanguage: "json",
              value,
              onChange: handleEditorChange,
              theme: theme === "dark" ? "vs-dark" : "light",
              options: {
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 13,
                lineNumbers: "on",
                folding: false,
                glyphMargin: false,
                lineDecorationsWidth: 0,
                lineNumbersMinChars: 3,
                renderLineHighlight: "none",
                scrollbar: {
                  vertical: "auto",
                  horizontal: "auto",
                  verticalScrollbarSize: 10,
                  horizontalScrollbarSize: 10
                },
                overviewRulerLanes: 0,
                hideCursorInOverviewRuler: true,
                overviewRulerBorder: false,
                readOnly,
                formatOnPaste: true,
                formatOnType: true,
                automaticLayout: true,
                wordWrap: "on"
              }
            }
          )
        }
      ),
      error && /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: error })
    ] });
  }
);
JsonEditor.displayName = "JsonEditor";
function FilterAccordion({
  activeFiltersCount,
  onClearFilters,
  children,
  defaultOpen = false,
  title = "Filtros"
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return /* @__PURE__ */ jsxs(Card, { children: [
    /* @__PURE__ */ jsx(
      CardHeader,
      {
        className: "cursor-pointer hover:bg-accent/50 transition-colors",
        onClick: () => setIsOpen(!isOpen),
        children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx(Filter, { className: "h-5 w-5" }),
            /* @__PURE__ */ jsx(CardTitle, { className: "text-lg", children: title }),
            activeFiltersCount > 0 && /* @__PURE__ */ jsx(Badge, { variant: "default", className: "bg-primary", children: activeFiltersCount })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            activeFiltersCount > 0 && /* @__PURE__ */ jsxs(
              Button,
              {
                variant: "ghost",
                size: "sm",
                onClick: (e) => {
                  e.stopPropagation();
                  onClearFilters();
                },
                children: [
                  /* @__PURE__ */ jsx(X, { className: "h-4 w-4 mr-1" }),
                  "Limpar"
                ]
              }
            ),
            isOpen ? /* @__PURE__ */ jsx(ChevronUp, { className: "h-5 w-5 text-muted-foreground" }) : /* @__PURE__ */ jsx(ChevronDown, { className: "h-5 w-5 text-muted-foreground" })
          ] })
        ] })
      }
    ),
    isOpen && /* @__PURE__ */ jsx(CardContent, { className: "animate-in slide-in-from-top-2 mt-4", children })
  ] });
}
function MonthYearSelector({
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
  disableFutureMonths = false,
  minYear = 2026
}) {
  const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
  const currentMonth = (/* @__PURE__ */ new Date()).getMonth() + 1;
  const endYear = Math.max(currentYear, minYear);
  const yearCount = endYear - minYear + 1;
  const years = Array.from({ length: yearCount }, (_, i) => minYear + i);
  const monthNames = [
    "Janeiro",
    "Fevereiro",
    "Mar\xE7o",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro"
  ];
  const isMonthDisabled = (monthIndex) => {
    if (!disableFutureMonths) return false;
    if (selectedYear < currentYear) return false;
    if (selectedYear > currentYear) return true;
    return monthIndex > currentMonth;
  };
  return /* @__PURE__ */ jsx(Card, { className: "w-auto", children: /* @__PURE__ */ jsx(CardContent, { children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(Calendar, { className: "h-4 w-4 text-muted-foreground" }),
    /* @__PURE__ */ jsx(
      "select",
      {
        value: selectedMonth,
        onChange: (e) => onMonthChange(Number(e.target.value)),
        className: "h-9 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent",
        children: monthNames.map((name, index) => /* @__PURE__ */ jsx(
          "option",
          {
            value: index + 1,
            disabled: isMonthDisabled(index + 1),
            children: name
          },
          index + 1
        ))
      }
    ),
    /* @__PURE__ */ jsx(
      "select",
      {
        value: selectedYear,
        onChange: (e) => onYearChange(Number(e.target.value)),
        className: "h-9 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent",
        children: years.map((year) => /* @__PURE__ */ jsx("option", { value: year, children: year }, year))
      }
    )
  ] }) }) });
}
var CurrencyInput = forwardRef(
  ({ label, value, onChange, error, helperText, className, required, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    useEffect(() => {
      if (value === 0) {
        setDisplayValue("");
      } else {
        setDisplayValue(formatToBRL(value));
      }
    }, [value]);
    const handleChange = (e) => {
      const rawValue = e.target.value;
      const numericValue = parseFromBRL(rawValue);
      onChange(numericValue);
    };
    const handleBlur = () => {
      if (value > 0) {
        setDisplayValue(formatToBRL(value));
      }
    };
    return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsxs("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5", children: [
        label,
        required && /* @__PURE__ */ jsx("span", { className: "text-red-500 ml-1", children: "*" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        /* @__PURE__ */ jsx("span", { className: "absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium text-sm", children: "R$" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            ref,
            value: displayValue,
            onChange: handleChange,
            onBlur: handleBlur,
            className: cn(
              "flex h-10 w-full pl-10 pr-3 rounded-md border border-input bg-background py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              error && "border-red-500 focus-visible:ring-red-500",
              className
            ),
            ...props
          }
        )
      ] }),
      helperText && !error && /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-xs text-gray-500 dark:text-gray-400", children: helperText }),
      error && /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-sm text-destructive", children: error })
    ] });
  }
);
CurrencyInput.displayName = "CurrencyInput";
function StatsCard({
  title,
  value,
  description,
  icon: Icon,
  iconColor = "text-primary",
  trend
}) {
  return /* @__PURE__ */ jsxs(Card, { children: [
    /* @__PURE__ */ jsxs(CardHeader, { className: "flex flex-row items-center justify-between space-y-0 pb-2", children: [
      /* @__PURE__ */ jsx(CardTitle, { className: "text-sm font-medium text-muted-foreground", children: title }),
      /* @__PURE__ */ jsx(Icon, { className: cn("h-5 w-5", iconColor) })
    ] }),
    /* @__PURE__ */ jsxs(CardContent, { children: [
      /* @__PURE__ */ jsx("div", { className: "text-2xl font-bold text-card-foreground", children: value }),
      description && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground mt-1", children: description }),
      trend && /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center text-xs", children: [
        /* @__PURE__ */ jsxs(
          "span",
          {
            className: cn(
              "font-medium",
              trend.value >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
            ),
            children: [
              trend.value >= 0 ? "+" : "",
              trend.value,
              "%"
            ]
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "text-muted-foreground ml-1", children: trend.label })
      ] })
    ] })
  ] });
}

// src/tokens/colors.ts
var colors2 = {
  brand: {
    primary: "#B05D41",
    // Marrom Ébano
    secondary: "#DED1BD",
    // Bege Aveludado
    background: "#FAF7F2",
    // Off White Luxuoso
    foreground: "#240404"
    // Terracota Elegante
  },
  light: {
    background: "hsl(38, 44%, 96%)",
    foreground: "hsl(0, 80%, 8%)",
    card: "hsl(0, 0%, 100%)",
    cardForeground: "hsl(0, 80%, 8%)",
    popover: "hsl(0, 0%, 100%)",
    popoverForeground: "hsl(0, 80%, 8%)",
    primary: "hsl(15, 47%, 47%)",
    primaryForeground: "hsl(38, 44%, 96%)",
    secondary: "hsl(36, 35%, 81%)",
    secondaryForeground: "hsl(0, 80%, 8%)",
    muted: "hsl(36, 30%, 85%)",
    mutedForeground: "hsl(0, 30%, 40%)",
    accent: "hsl(36, 35%, 81%)",
    accentForeground: "hsl(0, 80%, 8%)",
    destructive: "hsl(0, 70%, 50%)",
    destructiveForeground: "hsl(38, 44%, 96%)",
    border: "hsl(36, 25%, 88%)",
    input: "hsl(36, 25%, 88%)",
    ring: "hsl(15, 47%, 47%)"
  },
  dark: {
    background: "hsl(0, 50%, 6%)",
    foreground: "hsl(38, 44%, 96%)",
    card: "hsl(0, 40%, 10%)",
    cardForeground: "hsl(38, 44%, 96%)",
    popover: "hsl(0, 40%, 10%)",
    popoverForeground: "hsl(38, 44%, 96%)",
    primary: "hsl(15, 47%, 47%)",
    primaryForeground: "hsl(38, 44%, 96%)",
    secondary: "hsl(15, 30%, 20%)",
    secondaryForeground: "hsl(38, 44%, 96%)",
    muted: "hsl(0, 30%, 18%)",
    mutedForeground: "hsl(36, 20%, 65%)",
    accent: "hsl(15, 45%, 35%)",
    accentForeground: "hsl(38, 44%, 96%)",
    destructive: "hsl(0, 70%, 50%)",
    destructiveForeground: "hsl(38, 44%, 96%)",
    border: "hsl(0, 30%, 18%)",
    input: "hsl(0, 30%, 18%)",
    ring: "hsl(15, 47%, 47%)"
  }
};

// src/tokens/spacing.ts
var spacing = {
  0: "0",
  px: "1px",
  0.5: "0.125rem",
  // 2px
  1: "0.25rem",
  // 4px
  1.5: "0.375rem",
  // 6px
  2: "0.5rem",
  // 8px
  2.5: "0.625rem",
  // 10px
  3: "0.75rem",
  // 12px
  3.5: "0.875rem",
  // 14px
  4: "1rem",
  // 16px
  5: "1.25rem",
  // 20px
  6: "1.5rem",
  // 24px
  7: "1.75rem",
  // 28px
  8: "2rem",
  // 32px
  9: "2.25rem",
  // 36px
  10: "2.5rem",
  // 40px
  11: "2.75rem",
  // 44px
  12: "3rem",
  // 48px
  14: "3.5rem",
  // 56px
  16: "4rem",
  // 64px
  20: "5rem",
  // 80px
  24: "6rem",
  // 96px
  28: "7rem",
  // 112px
  32: "8rem",
  // 128px
  36: "9rem",
  // 144px
  40: "10rem",
  // 160px
  44: "11rem",
  // 176px
  48: "12rem",
  // 192px
  52: "13rem",
  // 208px
  56: "14rem",
  // 224px
  60: "15rem",
  // 240px
  64: "16rem",
  // 256px
  72: "18rem",
  // 288px
  80: "20rem",
  // 320px
  96: "24rem"
  // 384px
};
var borderRadius = {
  none: "0",
  sm: "0.125rem",
  // 2px
  DEFAULT: "0.25rem",
  // 4px
  md: "0.375rem",
  // 6px
  lg: "0.5rem",
  // 8px
  xl: "0.75rem",
  // 12px
  "2xl": "1rem",
  // 16px
  "3xl": "1.5rem",
  // 24px
  full: "9999px"
};

export { Badge, Breadcrumbs, Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Checkbox, ConfirmDialog, ConfirmationModal, CurrencyInput, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, FilterAccordion, Input, JsonEditor, MonthYearSelector, Pagination, PasswordStrength, PhoneInput, Select, SortableTable, Spinner, StatsCard, Switch, TabFilter, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, ThemeToggle, Toast, applyPhoneMask, badgeVariants, borderRadius, buttonVariants, cn, colors2 as colors, copyToClipboard, formatCurrency, formatNumber, formatPercentage, formatToBRL, parseFromBRL, removeMask, spacing, truncate, validatePassword };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map